import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, ArrowUpRight, Clock, HeartPulse, Inbox, Leaf, Minus, TrendingUp, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, XAxis } from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { can, useOrg } from "../org";
import { Initials, displayName } from "../people/bits";
import { churchHour, fmtInChurch } from "../care/text";
import { ListSheet } from "./ListSheet";
import { CareTeam } from "./CareTeam";
import { OverviewAssignments } from "../care/Assignments";

const H = ({ children }) => <h2 className="t-heading">{children}</h2>;
const Card = ({ className, children }) => <div className={cn("surface", className)}>{children}</div>;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const shortDay = (iso) => fmtInChurch(`${iso}T12:00:00Z`, { month: "short", day: "numeric", timeZone: "UTC" });

const move = (now, then, goodUp, unit = "") => {
  if (now === then) return { Icon: Minus, text: "No change", tone: "" };
  const up = now > then;
  return { Icon: up ? ArrowUpRight : ArrowDownRight, text: `from ${then}${unit} last week`, tone: up === goodUp ? "text-calm" : "text-sun" };
};

const Tile = ({ label, value, mv, detail, onOpen, testid }) => (
  <button onClick={onOpen} className="text-left flex flex-col bg-paper p-5 sm:p-6 hover:bg-surface transition-colors" data-testid={testid}>
    <p className="text-ink-soft text-[15px] leading-snug">{label}</p>
    <p className="t-metric mt-3" data-testid={`${testid}-value`}>{value}</p>
    {mv && <p className={cn("trend mt-2.5", mv.tone)} data-testid={`${testid}-move`}><mv.Icon size={15} />{mv.text}</p>}
    <p className="text-sm text-ink-faint mt-1 leading-snug">{detail}</p>
  </button>
);

const Summary = ({ d, openList }) => {
  const m = d.metrics, t = d.trend, prev = t[t.length - 2] || t[t.length - 1];
  const period = d.days === 7 ? "this week" : `in the last ${d.days} days`;
  return (
    <section data-testid="overview-summary">
      <H>How are we doing?</H>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border-y border-line mt-5">
        <Tile testid="metric-coverage" label="Care coverage" value={`${m.coverage_pct}%`} mv={move(m.coverage_pct, prev.coverage, true, "%")} detail={`${m.within} of ${m.monitored} people within their Care Rhythm`} onOpen={() => openList("within", "Within their Care Rhythm")} />
        <Tile testid="metric-attention" label="Need attention" value={m.attention} detail="Follow-ups due or past due, or outside their rhythm" onOpen={() => openList("attention", "People needing attention")} />
        <Tile testid="metric-connected" label={`Connected ${period}`} value={m.connected} mv={d.days === 7 ? move(m.connected, m.connected_prev, true) : null} detail={`${plural(m.connections, "meaningful care connection", "meaningful care connections")} logged`} onOpen={() => openList("connected", `Connected ${period}`)} />
        <Tile testid="metric-unassigned" label="Unassigned needs" value={m.unassigned_needs} detail="Open care needs waiting for an owner" onOpen={() => document.querySelector("[data-testid=overview-unassigned]")?.scrollIntoView({ behavior: "smooth", block: "center" })} />
      </div>
    </section>
  );
};

const AssignNeed = ({ need, onDone }) => {
  const { api } = useOrg();
  const [list, setList] = useState([]);
  const load = () => !list.length && api("/caregivers").then(setList).catch(() => {});
  const pick = (c) => api(`/needs/${need.id}`, { method: "PATCH", body: { owner_id: c.id } }).then(() => { toast.success(`${c.name} will care for ${need.person.first_name}`); onDone(); }).catch((e) => toast.error(e.message));
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger className="btn btn-outline btn-sm shrink-0" data-testid={`assign-need-${need.id}`}>Assign</PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-2xl bg-surface border-line p-2" data-testid={`assign-need-menu-${need.id}`}>
        <p className="px-3 pt-2 pb-1 text-sm text-ink-faint">Who could care for {need.person.first_name}?</p>
        {list.map((c) => (
          <button key={c.id} onClick={() => pick(c)} className="w-full flex justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-sunk text-left" data-testid={`assign-need-option-${c.id}`}>
            <span className="text-ink font-medium">{c.name}</span><span className={cn("text-sm", c.caring_for >= c.comfortable_load ? "text-sun" : "text-ink-faint")}>caring for {c.caring_for}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const GapRow = ({ icon: Icon, title, hint, onOpen, testid }) => (
  <button onClick={onOpen} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={testid}>
    <span className="h-11 w-11 rounded-full bg-sunk text-ink-soft flex items-center justify-center shrink-0"><Icon size={20} /></span>
    <span className="flex-1 min-w-0"><span className="block t-name-sm group-hover:text-brand transition-colors">{title}</span><span className="block text-sm text-ink-faint mt-0.5 truncate">{hint}</span></span>
    <ArrowRight size={18} className="text-ink-faint group-hover:text-brand shrink-0" />
  </button>
);

const Gaps = ({ d, openList, reload }) => {
  const m = d.metrics;
  const names = (k) => { const ids = d.lists[k] || []; const f = ids.slice(0, 2).map((i) => displayName(d.people[i])).join(", "); return ids.length > 2 ? `${f} and ${ids.length - 2} more` : f || "No one right now"; };
  const n = d.unassigned_needs.length;
  return (
    <section data-testid="overview-gaps">
      <H>Where are the gaps?</H>
      <Card className="p-6 mt-4">
        <div className="flex gap-4" data-testid="overview-unassigned">
          <span className="h-11 w-11 rounded-full bg-brand-soft text-brand flex items-center justify-center shrink-0"><Inbox size={20} /></span>
          <div><p className="t-name-sm" data-testid="overview-unassigned-title">{n ? `${plural(n, "care need is", "care needs are")} waiting for someone` : "Every open care need has someone"}</p><p className="text-sm text-ink-faint mt-0.5">No one has taken these yet.</p></div>
        </div>
        <div className="mt-2 divide-y divide-line">
          {d.unassigned_needs.map((u) => (
            <div key={u.id} className="flex items-center gap-3 py-3.5" data-testid={`unassigned-need-${u.id}`}>
              <Initials p={u.person} size={42} />
              <Link to={`/people/${u.person.id}`} className="flex-1 min-w-0"><p className="text-ink font-medium leading-tight">{displayName(u.person)}</p><p className="text-sm text-ink-faint leading-snug truncate">{u.title} · {fmtInChurch(u.opened_at, { month: "short", day: "numeric" })}</p></Link>
              <AssignNeed need={u} onDone={reload} />
            </div>
          ))}
        </div>
        <div className="mt-2 border-t border-line divide-y divide-line">
          <GapRow icon={HeartPulse} testid="gap-attention" title={`${plural(m.attention, "person needs", "people need")} attention`} hint={names("attention")} onOpen={() => openList("attention", "People needing attention")} />
          <GapRow icon={Clock} testid="gap-overdue" title={`${plural(m.overdue, "follow-up is", "follow-ups are")} past their date`} hint={names("overdue")} onOpen={() => openList("overdue", "Follow-ups past their date")} />
          <GapRow icon={Leaf} testid="gap-outside" title={`${plural(m.outside, "person is", "people are")} outside their Care Rhythm`} hint={names("outside")} onOpen={() => openList("outside", "Outside their Care Rhythm")} />
          <GapRow icon={UserRound} testid="gap-no-caregiver" title={`${plural(m.no_caregiver, "person doesn't", "people don't")} have an Assigned Care Leader`} hint={names("no_caregiver")} onOpen={() => openList("no_caregiver", "People without an Assigned Care Leader")} />
        </div>
      </Card>
    </section>
  );
};

const SERIES = { connected: "People connected", coverage: "Care coverage", outside: "Outside rhythm", active: "Active care" };

const Movement = ({ icon: Icon, children, onOpen, testid }) => (
  <button onClick={onOpen} className="w-full flex items-start gap-3 py-3 text-left group" data-testid={testid}>
    <Icon size={18} className="text-calm mt-1 shrink-0" /><span className="flex-1 text-[16px] text-ink leading-snug group-hover:text-brand transition-colors">{children}</span><ArrowRight size={16} className="text-ink-faint mt-1 shrink-0" />
  </button>
);

const word = (now, then, up = "up", down = "down") => (now === then ? "the same as" : now > then ? `${up} from` : `${down} from`);

const Changing = ({ d, openList }) => {
  const [s, setS] = useState("connected");
  const t = d.trend, now = t[t.length - 1], wk = t[t.length - 2] || now, mo = t[t.length - 5] || t[0];
  const data = t.map((x) => ({ v: x[s], label: shortDay(x.week_end) }));
  return (
    <section data-testid="overview-changing">
      <H>What is changing?</H>
      <Card className="p-6 mt-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(SERIES).map(([k, l]) => <button key={k} onClick={() => setS(k)} className={cn("h-9 px-4 rounded-full text-sm font-medium", s === k ? "bg-ink text-paper" : "bg-sunk text-ink-soft")} data-testid={`trend-${k}`}>{l}</button>)}
        </div>
        <p className="text-sm text-ink-faint mt-4">Last 12 weeks, week by week</p>
        <div className="h-36 mt-2 -mx-2" data-testid="trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: 8, right: 8, top: 6, bottom: 0 }}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} interval={3} padding={{ left: 20, right: 20 }} tick={{ fill: "var(--ink-faint)", fontSize: 12 }} />
              <Area type="monotone" dataKey="v" stroke="var(--brand)" strokeWidth={2.5} fill="var(--brand-soft)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 border-t border-line divide-y divide-line">
          <Movement icon={TrendingUp} testid="move-connected" onOpen={() => openList("connected", "Connected recently")}><b>{plural(now.connected, "person was", "people were")}</b> meaningfully connected with this week, {word(now.connected, wk.connected)} {wk.connected} last week.</Movement>
          <Movement icon={Leaf} testid="move-coverage" onOpen={() => openList("within", "Within their Care Rhythm")}>Care coverage is <b>{now.coverage}%</b>, {word(now.coverage, mo.coverage)} {mo.coverage}% a month ago.</Movement>
          <Movement icon={Clock} testid="move-outside" onOpen={() => openList("outside", "Outside their Care Rhythm")}>People outside their rhythm went from {mo.outside} to <b>{now.outside}</b> this month.</Movement>
          <Movement icon={HeartPulse} testid="move-active" onOpen={() => openList("needs_open", "People with an open care need")}>Active care needs: <b>{now.active}</b> now, {word(now.active, mo.active)} {mo.active} a month ago.</Movement>
        </div>
      </Card>
    </section>
  );
};

export default function Overview() {
  const { api, me, membership, onTenantError } = useOrg();
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  const [list, setList] = useState(null);
  const load = useCallback(() => api(`/overview?days=${days}`).then(setD).catch((e) => { onTenantError(e); toast.error(e.message); }), [api, days]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const openList = (key, title) => setList({ key, title });
  const h = churchHour();
  if (!d) return <p className="t-meta" data-testid="overview-loading">Loading…</p>;
  const n = d.unassigned_needs.length;
  return (
    <div data-testid="overview-screen">
      <p className="t-label">{membership.org_name} · Overview</p>
      <h1 className="t-greeting mt-3">Good {h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"}, {me.user.name.split(" ")[0]}.</h1>
      <p className="text-xl text-ink-soft mt-4 leading-relaxed max-w-2xl" data-testid="overview-headline">
        <button onClick={() => openList("attention", "People needing attention")} className="text-ink font-semibold underline decoration-line underline-offset-4 hover:text-brand" data-testid="overview-attention-btn">{plural(d.metrics.attention, "person needs", "people need")} attention</button>{" "}
        and {plural(n, "care need is", "care needs are")} waiting for someone.
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-[15px] text-ink-faint">
        <span>Church Care Rhythm: every {d.rhythm_days} days{can(membership).directory && <> · <Link to="/settings" className="text-brand font-medium underline underline-offset-4" data-testid="overview-rhythm-change">Change</Link></>}</span>
        <span className="inline-flex p-0.5 rounded-[10px] bg-sunk" data-testid="overview-period">
          {[7, 30].map((x) => <button key={x} aria-pressed={days === x} onClick={() => setDays(x)} className={cn("h-10 px-4 rounded-[8px] text-sm font-medium", days === x ? "bg-surface text-ink shadow-soft" : "text-ink-soft")} data-testid={`overview-period-${x}`}>{x === 7 ? "This week" : "30 days"}</button>)}
        </span>
      </div>
      <div className="mt-8"><Summary d={d} openList={openList} /></div>
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-8 items-start [&>*]:min-w-0">
        <Gaps d={d} openList={openList} reload={load} />
        <Changing d={d} openList={openList} />
      </div>
      <div className="mt-14"><CareTeam d={d} openList={openList} /></div>
      {d.assignments && <div className="mt-14"><OverviewAssignments a={d.assignments} /></div>}
      <ListSheet list={list} data={d} onClose={() => setList(null)} />
    </div>
  );
}
